import * as React from 'react';
import * as moment from 'moment';
import { SurveyInstanceDataService, UserStyleDataService, StyleDataService, SurveyVersionDataService } from '../../services';
import { SurveyAnswer, UserStyle, Style, SurveyInstance, SurveyVersion, UserPermissions } from '../../models';
import { DataGrid, LoadingIndicator, PVAPage } from '../../components';
import { BaseRouteProps } from '../../interfaces';
import { FRONTEND_ROOT } from '../../config/host-config';
import { Gender, EducationLevel } from '../../enums';
import { UserStore } from '../../stores';
import { Roles } from '../../stores/Constants';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';

interface SurveyInstanceViewState {
    answers: SurveyAnswer[];
    userStyle: UserStyle | undefined;
    styles: Style[];
    answersLoading: boolean;
    surveyInstance?: SurveyInstance;
    surveyVersionOptions: { label: string, value: any }[];
    exportLoading: boolean;
    canEditUserStyle: boolean;
}

interface SurveyInstanceProps extends BaseRouteProps<{ id: string }> {
    UserStore?: UserStore;
}

@inject('UserStore') @observer
export class SurveyInstanceView extends React.Component<SurveyInstanceProps, SurveyInstanceViewState> {
    private surveyVersionService = new SurveyVersionDataService(this.props.notificationService);
    private surveyInstanceDataService = new SurveyInstanceDataService(this.props.notificationService);
    private userStyleDataService = new UserStyleDataService(this.props.notificationService);
    private styleDataService = new StyleDataService(this.props.notificationService);

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    constructor(props: SurveyInstanceProps) {
        super(props);

        this.state = {
            answers: new Array<SurveyAnswer>(),
            userStyle: undefined,
            styles: [],
            answersLoading: true,
            surveyVersionOptions: [],
            exportLoading: false,
            canEditUserStyle: false
        };
    }

    public componentDidMount() {
        this.surveyInstanceDataService.getSurveyVersionInfo(this.props.match.params.id)
            .then((response: SurveyInstance) => {
                this.setState(
                    { surveyInstance: response }
                );

                if (response.surveyVersion) {
                    this.surveyVersionService.getAllBySurvey(response.surveyVersion.survey).then(
                        (surveyVersions: SurveyVersion[]) => {
                            const options = surveyVersions.filter((sv: SurveyVersion) => {
                                if (!sv.published && !this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin, Roles.PonderaAssociate])) {
                                    return false;
                                }
                                return true;
                            }).map(surveyVersion => {
                                if (surveyVersion.active) {
                                    return { label: `#${surveyVersion.version} ${surveyVersion.nickname ? surveyVersion.nickname  : ''} (active)`, value: surveyVersion.id };
                                } else if (surveyVersion.published) {
                                    return { label: `#${surveyVersion.version} ${surveyVersion.nickname ? surveyVersion.nickname  : ''}`, value: surveyVersion.id };
                                } else {
                                    return { label: `#${surveyVersion.version} ${surveyVersion.nickname ? surveyVersion.nickname  : ''} *`, value: surveyVersion.id };
                                }
                            });

                            this.setState({ surveyVersionOptions: options });
                        }
                    );
                }
            })
            .catch(() => {
                console.log('unauthorized');
            });

        if (!this.props.UserStore!.userRoleIn([Roles.OrgAdmin, Roles.Manager, Roles.User])) {
            this.surveyInstanceDataService.getAnswers(this.props.match.params.id)
                .then((answers: SurveyAnswer[]) => {
                    this.setState(
                        { answers: answers, answersLoading: false }
                    );
                }).catch(() => {
                    this.props.notificationService.addError('An error occurred while loading response data for survey. Please try refreshing the page.');
                    this.setState(
                        { answersLoading: false }
                    );
                });
        } else {
            this.setState(
                { answersLoading: false }
            );
        }

        this.styleDataService.getBySurveyInstance(this.props.match.params.id).then((styles: Style[]) => {
            this.setState({
                styles: styles
            });
        });

        this.userStyleDataService.getBySurveyInstance(this.props.match.params.id).then((userStyle: UserStyle) => {
            if (userStyle) {
                this.setState({
                    userStyle: userStyle,
                    canEditUserStyle: true
                });
            } else {
                this.setState({
                    userStyle: undefined,
                    canEditUserStyle: true
                });
            }
        })
        .catch(() => {
            console.log('unauthorized');
            this.setState({
                canEditUserStyle: false
            });
        });
    }

    private userStyleChanged(e: any) {
        var styleId: number = Number(e.target.value);
        this.userStyleDataService.updateBySurveyInstance(this.props.match.params.id, styleId).then((userStyle: UserStyle) => {
            if (userStyle) {
                this.props.notificationService.addNotification('PVA style overridden successfully.');
                this.setState({
                    userStyle: userStyle
                });
            } else {
                this.props.notificationService.addNotification('PVA style override removed successfully.');
                this.setState({
                    userStyle: undefined
                });
            }
        });
    }

    private csv() {
        this.setState({exportLoading: true});

        this.surveyInstanceDataService.getCSV(this.props.match.params.id, this.props.match.params.id)
            .then(() => {
                this.setState({exportLoading: false});
            })
            .catch(() => {
                this.setState({exportLoading: false});
                this.props.notificationService.addError('Error exporting answers to CSV.');
            });
    }

    private pdf() {
        this.setState({exportLoading: true});

        this.surveyInstanceDataService.getPDF(this.props.match.params.id, this.props.match.params.id)
            .then(() => {
                this.setState({exportLoading: false});
            })
            .catch(() => {
                this.setState({exportLoading: false});
                this.props.notificationService.addError('Error exporting answers to PDF.');
            });
    }

    private setActive() {
        if (this.state.surveyInstance) {
            this.surveyInstanceDataService.setActive(this.state.surveyInstance.id).then(() => {
                if (this.state.surveyInstance) {
                    this.state.surveyInstance.active = true;
                    this.setState({
                        surveyInstance: this.state.surveyInstance
                    });
                }
            });
        }
    }

    newSurveyVersionChanged = (e: any) => {
        this.surveyInstanceDataService.updateSurveyVersion(this.state.surveyInstance!.id, e.target.value)
            .then((newSurveyVersion: SurveyVersion) => {
                this.props.notificationService.addNotification('Survey version updated successfully.');

                this.setState({
                    surveyInstance: Object.assign({}, this.state.surveyInstance, { surveyVersion: newSurveyVersion })
                });
            });
    }

    private answersBinary() {

        const listData = this.state.answers.filter((entity: SurveyAnswer) => {
                return entity.questionType === 'binary';
            }).map((entity: SurveyAnswer) => {
            return {
                'Identifier': entity.questionNum,
                'Statement': entity.questionText,
                'Option': entity.optionText,
                'Value': entity.optionValue
            };
        });

        const columnDefs = [
            { id: 'Identifier', title: '#' },
            { id: 'Statement' },
            { id: 'Option' },
            { id: 'Value' }
        ];

        return <DataGrid entities={listData} columns={columnDefs} />;
    }

    private answersText() {

        const listData = this.state.answers.filter((entity: SurveyAnswer) => {
                return entity.questionType === 'text' || entity.questionType === 'freeText';
            }).map((entity: SurveyAnswer) => {
            return {
                'Identifier': entity.questionNum,
                'Statement': entity.questionText,
                'Response': entity.optionText
            };
        });

        const columnDefs = [
            { id: 'Identifier', title: '#' },
            { id: 'Statement' },
            { id: 'Response' }
        ];

        return <DataGrid entities={listData} columns={columnDefs} />;
    }

    private answersLikert() {

        const listData = this.state.answers.filter((entity: SurveyAnswer) => {
                return entity.questionType === 'scale';
            }).map((entity: SurveyAnswer) => {
            return {
                'Identifier': entity.questionNum,
                'Statement': entity.questionText,
                'Option': entity.optionText,
                'Value': entity.optionValue
            };
        });

        const columnDefs = [
            { id: 'Identifier', title: '#' },
            { id: 'Statement' },
            { id: 'Option' },
            { id: 'Value' }
        ];

        return <DataGrid entities={listData} columns={columnDefs} />;
    }

    private getBirthPlaceString (city?: string, state?: string, country?: string) {
        var birthPlaceTokens: string[] = [];

        if (city) { birthPlaceTokens.push(city); }
        if (state) { birthPlaceTokens.push(state); }
        if (country) { birthPlaceTokens.push(country); }

        return birthPlaceTokens.join(', ');
    }

    private getDemographicsSection () {
        const genderOptions = [
            { label: 'Prefer Not To Answer', value: Gender.preferNotToAnswer },
            { label: 'Female', value: Gender.female },
            { label: 'Male', value: Gender.male }
        ];

        const educationLevelOptions = [
            { label: 'Prefer Not To Answer', value: EducationLevel.preferNotToAnswer },
            { label: '8th Grade or Less', value: EducationLevel.eigthGradeOrLess },
            { label: '9th Grade', value: EducationLevel.ninthGrade },
            { label: '10th Grade', value: EducationLevel.tenthGrade },
            { label: '11th Grade', value: EducationLevel.eleventhGrade },
            { label: '12th Grade', value: EducationLevel.twelfthGrade },
            { label: 'College Freshman', value: EducationLevel.collegeFreshman },
            { label: 'College Sophomore', value: EducationLevel.collegeSophomore },
            { label: 'College Junior', value: EducationLevel.collegeJunior },
            { label: 'College Graduate', value: EducationLevel.collegeGraduate },
            { label: `Master's`, value: EducationLevel.masters },
            { label: 'Doctorate', value: EducationLevel.doctorate },
        ];

        let demographicsComponent = null;

        if (this.state.surveyInstance && this.state.surveyInstance.demographics) {
            const { title, gender, birthDate, city, state, country, educationLevel } = this.state.surveyInstance.demographics;
            const labelWidth = 'col-sm-3', valueWidth = 'col-sm-9';

            let genderText = '';
            if (gender) {
                const option =  genderOptions.find(x => Gender[x.value] === gender.toString());

                if (option) { genderText = option.label; }
            }

            let educationLevelText = '';
            if (educationLevel) {
                const option =  educationLevelOptions.find(x => EducationLevel[x.value] === educationLevel.toString());

                if (option) { educationLevelText = option.label; }
            }

            demographicsComponent = (
                <>
                    <h3>Demographic Responses</h3>
                    <form className="form-horizontal">
                        { title &&
                            <div className="form-group">
                                <label className={`${labelWidth} control-label`}>Title</label>
                                <div className={valueWidth}>
                                    <p className="form-control-static">{title}</p>
                                </div>
                            </div>
                        }
                        { gender &&
                            <div className="form-group">
                                <label className={`${labelWidth} control-label`}>Gender</label>
                                <div className={valueWidth}>
                                    <p className="form-control-static">{genderText}</p>
                                </div>
                            </div>
                        }
                        { birthDate &&
                            <div className="form-group">
                                <label className={`${labelWidth} control-label`}>Birth Date</label>
                                <div className={valueWidth}>
                                    <p className="form-control-static">{moment(birthDate).format('MM/DD/YYYY')}</p>
                                </div>
                            </div>
                        }
                        { (city || state || country) &&
                            <div className="form-group">
                                <label className={`${labelWidth} control-label`}>Birth Place</label>
                                <div className={valueWidth}>
                                    <p className="form-control-static">{this.getBirthPlaceString(city, state, country)}</p>
                                </div>
                            </div>
                        }
                        { educationLevel &&
                            <div className="form-group">
                                <label className={`${labelWidth} control-label`}>Education Level</label>
                                <div className={valueWidth}>
                                    <p className="form-control-static">{educationLevelText}</p>
                                </div>
                            </div>
                        }
                    </form>
                </>
            );
        }

        return demographicsComponent;
    }

    public render() {
        const { answers, answersLoading, userStyle, styles, surveyInstance, surveyVersionOptions } = this.state;

        // display loading indicator while loading the answers
        if (answersLoading) {
            return <LoadingIndicator />;
        }

        let headerButtons, bodyContent = null;
        if (answers.length === 0) {
            // if we have no answers, the survey hasn't been completed
            // so we'll just show a link to the survey
            bodyContent = (
                <div>
                    <h3>Survey Link</h3>
                    <div className="form-group">
                        <input type="text" className="form-control" readOnly value={`${FRONTEND_ROOT}/assessment/${this.props.match.params.id}`} />
                    </div>
                </div>
            );
        } else {
            headerButtons = (
                <PVAPage.Header.Buttons>
                    <button className="btn btn-default" onClick={(e) => this.csv()}>
                        <span className="glyphicon glyphicon-export" /> Export CSV
                    </button>
                    <button className="btn btn-default" onClick={(e) => this.pdf()}>
                        <span className="glyphicon glyphicon-export" /> Export PDF
                    </button>
                </PVAPage.Header.Buttons>
            );

            bodyContent = (
                <div>
                    <br />
                    {this.getDemographicsSection()}
                    <h3>True/False Answers</h3>
                    {this.answersBinary()}
                    <br />
                    <h3>Likert Answers</h3>
                    {this.answersLikert()}
                    <br />
                    <h3>Text Answers</h3>
                    {this.answersText()}
                </div>
            );
        }

        let changeVersionComponent = null;
        if (surveyInstance && surveyInstance.surveyVersion) {
            const { version, nickname } = surveyInstance.surveyVersion;
            let surveyVersionText = `#${version} ${nickname ? nickname  : ''}`;
            if (surveyInstance.surveyVersion.active) {
                surveyVersionText = `${surveyVersionText}*`;
            }
            changeVersionComponent = (
                <div>
                    {surveyVersionOptions.length > 1 && <div>
                        <h3>Change Survey Version</h3>

                        <div className="row">
                            <div className="col-sm-6">
                                <div className="form-group">
                                    <label>Current Version</label>
                                    <p className="form-control-static">{surveyVersionText}</p>
                                </div>
                            </div>
                            <div className="col-sm-6">
                                <div className="form-group">
                                    <label htmlFor="surveyVersionID">New Version</label>
                                    <select className="form-control" onChange={this.newSurveyVersionChanged} value={this.state.surveyInstance ? this.state.surveyInstance.surveyVersion.id : 0}>
                                        {surveyVersionOptions.map((item: any) => {
                                            return <option key={`sv-${item.label}`} value={item.value}>{item.label}</option>;
                                        })}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>}
                </div>
            );
        }

        var userStyleId: number = -1;
        if (userStyle) {
            userStyleId = userStyle.style.id;
        }

        return (
            <PVAPage>
                <PVAPage.Header>
                    <PVAPage.Header.Title>
                        <PVAPage.Header.Title.Text>
                            Survey Invite Info
                        </PVAPage.Header.Title.Text>
                    </PVAPage.Header.Title>
                    {headerButtons}
                </PVAPage.Header>

                <PVAPage.Body>
                    {this.state.exportLoading && <LoadingIndicator showPdfDescription={true} showInPanel={true} showBackground={true}/>}
                    {this.state.surveyInstance && this.state.surveyInstance.active && <span>Active Survey</span>}
                    {this.state.canEditUserStyle && this.state.surveyInstance && !this.state.surveyInstance.active && <div>
                        <div><i>This will mark this invite as the default when a user views their Individual Report</i></div>
                        <button className="btn btn-default" onClick={(e) => this.setActive()}>Set as Active Survey Invite</button>
                    </div>}
                    <h1>{this.state.surveyInstance && <span>{this.state.surveyInstance.user.firstName} {this.state.surveyInstance.user.lastName}</span>}</h1>
                    <h5>{this.state.surveyInstance && this.state.surveyInstance.user && this.state.surveyInstance.user.organization && <span>{this.state.surveyInstance.user.organization.name}</span>}</h5>
                    
                    {this.allowOverrideUserStyle() && <div>
                        <h3>User Style</h3>
                        {<select className="form-control" value={userStyleId} onChange={(e) => this.userStyleChanged(e)}>
                            <option value={-1}>[No Override]</option>
                            {styles.map((style: Style) => {
                                return <option key={style.id} value={style.id}>{style.name} ({style.core})</option>;
                            })}
                        </select>}
                    </div>}
                    {this.allowChangeSurveyVersion() && changeVersionComponent}
                    {bodyContent}
                </PVAPage.Body>
                <PVAPage.Footer />
            </PVAPage>);
    }

    allowChangeSurveyVersion() {
        if (!this.state.surveyInstance || !this.state.surveyInstance.surveyVersion) {
            return false;
        }

        if (this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin])) {
            return true;
        }

        if (this.props.UserStore!.checkUserPermission(UserPermissions.CanChangeSurveyVersionOfInvite)) {
            return true;
        }

        return false;
    }

    allowOverrideUserStyle() {
        if (!this.state.canEditUserStyle) {
            return false;
        }

        if (this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin])) {
            return true;
        }

        if (this.props.UserStore!.checkUserPermission(UserPermissions.CanOverrideStyleOfInvite)) {
            return true;
        }

        return false;
    }
}



// WEBPACK FOOTER //
// ./src/components/surveyinstance/SurveyInstanceView.tsx